import { useState, useEffect, useCallback } from 'react';
import type { IntegrationDto } from '../types';
import apiClient from '../shared/api/apiClient';

interface UseIntegrationsReturn {
    integrations: IntegrationDto[];
    loading: boolean;
    loadIntegrations: () => Promise<void>;
    addIntegration: (integration: IntegrationDto) => void;
    updateIntegration: (id: number, updates: Partial<IntegrationDto>) => void;
    deleteIntegration: (id: number) => void;
}

export const useIntegrations = (): UseIntegrationsReturn => {
    const [integrations, setIntegrations] = useState<IntegrationDto[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const loadIntegrations = useCallback(async () => {
        try {
            setLoading(true);

            const response = await apiClient.get<IntegrationDto[]>('/api/Integrations');

            setIntegrations(response.data || []);
        } catch (error) {
            console.error('Ошибка при загрузке интеграций:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Load integrations on mount
    useEffect(() => {
        loadIntegrations();
    }, [loadIntegrations]);

    // Integration state management functions
    const addIntegration = useCallback((integration: IntegrationDto) => {
        setIntegrations(prev => [...prev, integration]);
    }, []);

    const updateIntegration = useCallback((id: number, updates: Partial<IntegrationDto>) => {
        setIntegrations(prev => prev.map(integration =>
            integration.id === id ? { ...integration, ...updates } : integration
        ));
    }, []);

    const deleteIntegration = useCallback((id: number) => {
        setIntegrations(prev => prev.filter(integration => integration.id !== id));
    }, []);

    return {
        integrations,
        loading,
        loadIntegrations,
        addIntegration,
        updateIntegration,
        deleteIntegration,
    };
};